import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'R/Shiny Dashboards',
    'Data Viz (ggplot2, plotly)',
    'ETL Pipelines (GitLab CI, Docker, OpenShift)',
    'Bayesian Modeling (Stan/brms)',
    `Machine Learning (tidymodels)`,
    'CAR-T Cell Therapy',
    `CMC & Process Development`,
    'Experimental Design (DoE)',
    'NLP & Text Mining',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! My name is Sanjin (pronounced <em>sahn-yihn</em>) and I engineer data solutions
              for the biotech industry. With a background in Chemical Engineering, I spent the early
              part of my career in the wet lab at <strong>Cubist Pharmaceuticals</strong> (acquired
              by <strong>Merck</strong>), <strong>Sarepta Therapeutics</strong>, and{' '}
              <strong>bluebird bio</strong> (turned <strong>2seventybio</strong>).
            </p>

            <p>
              I transitioned into Data Science to solve a specific problem: the lack of robust
              analytics in process development.
            </p>

            <p>
              Today, I am a <strong>Principal Data Scientist</strong> at{' '}
              <a href="https://www.novartis.com/">Novartis</a>, focusing on the clinical development
              and commercialization of a next-gen, rapid CAR-T cell therapy process and product. I
              build tools that allow teams to see around corners—using{' '}
              <strong>predictive modeling</strong> and <strong>interactive dashboards</strong> to
              forecast manufacturing risks before they happen.
            </p>

            <p>
              From architecting automated ETL pipelines to developing Bayesian models for process
              characterization, I help turn raw data into regulatory-grade evidence.
            </p>

            <p>Here are a few technologies I've been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
